import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SubscriptionProvider } from "./context/SubscriptionContext";
import { Toaster } from "./components/ui/sonner";

// Eager: Auth + public entry points (must feel instant)
import { LoginPage } from "./components/auth/LoginPage";
import RegistrationPage from "./pages/RegistrationPage";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { PublicBookingPage } from "./components/booking/PublicBookingPage";
import { PublicLayout } from "./components/layout/PublicLayout";

// Layout (eager — rendered inside AdminLayout on every protected route)
import { Sidebar } from "./components/layout/Sidebar";
import { SubscriptionBanner } from "./components/subscription/SubscriptionBanner";
import { ImpersonationBanner } from "./components/subscription/ImpersonationBanner";
import { PageLoadingSkeleton } from "./components/layout/PageLoadingSkeleton";

// Lazy: auxiliary auth flows (rarely hit)
const OnboardingPage = lazy(() => import("./pages/OnboardingPage"));
const AcceptInvitationPage = lazy(() => import("./pages/AcceptInvitationPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const PublicFormPage = lazy(() => import("./pages/PublicFormPage"));
const KioskPage = lazy(() => import("./pages/KioskPage"));
const KioskWelcome = lazy(() => import("./pages/KioskWelcome"));

// Lazy: admin pages (split per route — bundle only loads when navigated to)
// Priority order (quickest wins first): PlatformPage, CoursesPage, SettingsPage, ReportsPage, then rest.
const PlatformPage = lazy(() => import("./pages/PlatformPage"));
const CoursesPage = lazy(() => import("./pages/CoursesPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const ReportsPage = lazy(() => import("./pages/ReportsPage"));
const DashboardPage = lazy(() =>
  import("./pages/DashboardPage").then((m) => ({ default: m.DashboardPage }))
);
const DiaryPage = lazy(() => import("./pages/DiaryPage"));
const ClientsPage = lazy(() => import("./pages/ClientsPage"));
const AppointmentsPage = lazy(() => import("./pages/AppointmentsPage"));
const ServicesPage = lazy(() => import("./pages/ServicesPage"));
const StaffPage = lazy(() => import("./pages/StaffPage"));
const FormsPage = lazy(() => import("./pages/FormsPage"));
const FormFillPage = lazy(() => import("./pages/FormFillPage"));
const FormTemplatePage = lazy(() => import("./pages/FormTemplatePage"));
const FormBuilderPage = lazy(() => import("./pages/FormBuilderPage"));
const PaymentsPage = lazy(() => import("./pages/PaymentsPage"));

// Admin layout wrapper
const AdminLayout = () => {
  return (
    <div className="min-h-screen bg-background" data-testid="admin-layout">
      <Sidebar />
      <div className="ml-64">
        <ImpersonationBanner />
        <SubscriptionBanner />
        <Suspense fallback={<PageLoadingSkeleton />}>
          <Outlet />
        </Suspense>
      </div>
    </div>
  );
};

// Auth redirect - if logged in, go to dashboard
const AuthRedirect = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <SubscriptionProvider>
      <BrowserRouter>
        <Suspense fallback={<PageLoadingSkeleton />}>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={
              <AuthRedirect>
                <LoginPage />
              </AuthRedirect>
            } />
            <Route path="/register" element={
              <AuthRedirect>
                <RegistrationPage />
              </AuthRedirect>
            } />

            {/* Invitation & password public flows */}
            <Route path="/accept-invitation/:token" element={<AcceptInvitationPage />} />
            <Route path="/forgot-password" element={
              <AuthRedirect>
                <ForgotPasswordPage />
              </AuthRedirect>
            } />
            <Route path="/reset-password/:token" element={<ResetPasswordPage />} />

            {/* Public booking page */}
            <Route path="/book" element={<PublicLayout />}>
              <Route index element={<PublicBookingPage />} />
            </Route>

            {/* Public form fill (no auth) */}
            <Route path="/form/:submissionId" element={<PublicFormPage />} />

            {/* Kiosk mode (no auth, iPad-optimised) */}
            <Route path="/kiosk/:submissionId" element={<KioskPage />} />
            <Route path="/kiosk/welcome" element={<KioskWelcome />} />

            {/* Platform admin (God Mode) - separate layout */}
            <Route path="/platform" element={
              <ProtectedRoute>
                <PlatformPage />
              </ProtectedRoute>
            } />

            {/* Onboarding wizard - full screen, no sidebar */}
            <Route path="/onboarding" element={
              <ProtectedRoute>
                <OnboardingPage />
              </ProtectedRoute>
            } />

            {/* Protected admin routes */}
            <Route path="/" element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="diary" element={<DiaryPage />} />
              <Route path="clients" element={<ClientsPage />} />
              <Route path="clients/new" element={<ClientsPage />} />
              <Route path="clients/:id" element={<ClientsPage />} />
              <Route path="appointments" element={<AppointmentsPage />} />
              <Route path="appointments/new" element={<AppointmentsPage />} />
              <Route path="appointments/:id" element={<AppointmentsPage />} />
              <Route path="services" element={<ServicesPage />} />
              <Route path="services/new" element={<ServicesPage />} />
              <Route path="services/:id" element={<ServicesPage />} />
              <Route path="staff" element={<StaffPage />} />
              <Route path="staff/new" element={<StaffPage />} />
              <Route path="staff/:id" element={<StaffPage />} />
              <Route path="forms" element={<FormsPage />} />
              <Route path="forms/new" element={<FormsPage />} />
              <Route path="forms/:id" element={<FormsPage />} />
              <Route path="forms/template/:templateId" element={<FormTemplatePage />} />
              <Route path="forms/builder" element={<FormBuilderPage />} />
              <Route path="forms/builder/:templateId" element={<FormBuilderPage />} />
              <Route path="forms/fill/:submissionId" element={<FormFillPage />} />
              <Route path="payments" element={<PaymentsPage />} />
              <Route path="courses" element={<CoursesPage />} />
              <Route path="courses/:id" element={<CoursesPage />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route path="settings" element={<SettingsPage />} />
            </Route>

            {/* Catch all - redirect to dashboard */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster position="top-right" />
      </SubscriptionProvider>
    </AuthProvider>
  );
}

export default App;
